/**
 * Analysis run requests: businesses and runs, the run detail read, owned
 * sources, evidence acquisition and the Business Profile review actions
 * (correct, approve, reject).
 */

import type { BusinessProfileDraft } from '@mouja/domain/analysis';

import type {
  AnalysisArtifactSummary,
  AnalysisBusiness,
  AnalysisBusinessSource,
  AnalysisDay2State,
  AnalysisDay3State,
  AnalysisDay4State,
  AnalysisDay5State,
  AnalysisDay6State,
  AnalysisDay7State,
  AnalysisDay8State,
  AnalysisRunSummary,
  AnalysisStageAttempt,
} from './analysis-run-state.js';
import { request } from './request.js';

export function fetchAnalysisBusinesses(): Promise<{
  businesses: readonly AnalysisBusiness[];
}> {
  return request('GET', '/workspace/analysis/businesses');
}

export function createAnalysisBusiness(
  idempotencyKey: string,
  body: {
    displayName: string;
    category?: string;
    geography?: string;
    offers: readonly {
      title: string;
      offerType: string;
      description?: string;
      deliveryMode?: string;
    }[];
    sources: readonly {
      platform: string;
      relationship: string;
      canonicalUrl: string;
      externalIdentity?: string;
    }[];
  },
): Promise<{ business: AnalysisBusiness }> {
  return request('POST', '/workspace/analysis/businesses', {
    body,
    idempotencyKey,
  });
}

export function fetchAnalysisRuns(): Promise<{
  runs: readonly AnalysisRunSummary[];
}> {
  return request('GET', '/workspace/analysis/runs');
}

export function createAnalysisRun(
  idempotencyKey: string,
  body: {
    targetBusinessId: string;
    primaryOfferId?: string;
    objective: string;
  },
): Promise<{ run: AnalysisRunSummary }> {
  return request('POST', '/workspace/analysis/runs', { body, idempotencyKey });
}

export function fetchAnalysisRun(runId: string): Promise<{
  run: AnalysisRunSummary;
  attempts: readonly AnalysisStageAttempt[];
  artifacts: readonly AnalysisArtifactSummary[];
  day2: AnalysisDay2State;
  day3: AnalysisDay3State;
  day4: AnalysisDay4State;
  day5: AnalysisDay5State;
  day6: AnalysisDay6State;
  day7: AnalysisDay7State;
  day8: AnalysisDay8State;
}> {
  return request('GET', `/workspace/analysis/runs/${encodeURIComponent(runId)}`);
}

export function addAnalysisSources(
  runId: string,
  idempotencyKey: string,
  sources: readonly {
    platform: string;
    canonicalUrl: string;
    externalIdentity?: string;
  }[],
): Promise<{ sources: readonly AnalysisBusinessSource[] }> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/sources`,
    {
      idempotencyKey,
      body: {
        sources: sources.map((source) => ({ ...source, relationship: 'OWNED' })),
      },
    },
  );
}

export function acquireAnalysisEvidence(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/acquire`,
    { idempotencyKey },
  );
}

export function correctAnalysisProfile(
  runId: string,
  idempotencyKey: string,
  body: { expectedArtifactId: string; profile: BusinessProfileDraft; reason?: string },
): Promise<{ draftArtifactId: string }> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/profile/correct`,
    { idempotencyKey, body },
  );
}

export function approveAnalysisProfile(
  runId: string,
  idempotencyKey: string,
  expectedArtifactId: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/profile/approve`,
    { idempotencyKey, body: { expectedArtifactId } },
  );
}

export function rejectAnalysisProfile(
  runId: string,
  idempotencyKey: string,
  body: { expectedArtifactId: string; reason: string },
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/profile/reject`,
    { idempotencyKey, body },
  );
}
